import type {
  GutBodyConnectionsCondition,
  GutBodyConnectionsOrgan,
  OrganLookupCode,
} from '@repo/types';
import { getDrawableOrgans } from '@repo/ui';
import { AgeGroup, getAgeGroup } from '@repo/utils';

import conditionsIntroTagsAdultFemale from 'assets/images/firstLook/conditions-intro-tags-adult-female.svg';
import conditionsIntroTagsAdultMale from 'assets/images/firstLook/conditions-intro-tags-adult-male.svg';
import conditionsIntroTagsAdultPregnant from 'assets/images/firstLook/conditions-intro-tags-adult-pregnant.svg';
import conditionsIntroTagsBabyToddler from 'assets/images/firstLook/conditions-intro-tags-baby-toddler.svg';
import conditionsIntroTagsChild from 'assets/images/firstLook/conditions-intro-tags-child.svg';

/**
 * Generic, educational system set for the intro slide — every drawable system
 * with a plain label. Rendered in purple via `GutBodyConnectionsBody`'s `purple` mode, so
 * the flag level is a neutral default. `GutBodyConnectionsBody` drops the codes that lack
 * artwork for the kit's age/sex/pregnancy, so this self-filters per body variant
 * just like the live conditions body.
 */
const GENERIC_BODY_LABELS: Record<OrganLookupCode, string> = {
  brain: 'Brain',
  endocrine: 'Hormones',
  gut: 'Digestion',
  heart: 'Heart',
  immune: 'Immune',
  kidney: 'Urinary',
  liver: 'Liver',
  lung: 'Respiratory',
  metabolic: 'Metabolic',
  muscle: 'Bone',
  oral: 'Oral',
  reproductive: 'Reproductive',
  skin: 'Skin',
};

export const GENERIC_BODY_ORGANS: GutBodyConnectionsOrgan[] = (
  Object.keys(GENERIC_BODY_LABELS) as OrganLookupCode[]
).map((lookup_code, index) => ({
  id: index + 1,
  lookup_code,
  display_name: GENERIC_BODY_LABELS[lookup_code],
  priority: index + 1,
  about: null,
  conditions: [],
  markers: [],
  flagged_count: 0,
  flag_level: 'low',
}));

export interface MarkedConditionSpotlight {
  organ: GutBodyConnectionsOrgan;
  condition: GutBodyConnectionsCondition;
}

/**
 * One spotlight per survey-marked condition, kept in the organ/condition order
 * the conditions page already sorts them. Conditions that don't map to an organ
 * never reach `useOrgans`, and organs the kit's body has no artwork for are
 * dropped, so a spotlight never shows a system the body cannot draw.
 */
export function selectMarkedConditionSpotlights(
  organs: GutBodyConnectionsOrgan[],
  body: Omit<Parameters<typeof getDrawableOrgans>[0], 'organs'>,
): MarkedConditionSpotlight[] {
  return getDrawableOrgans({ organs, ...body }).flatMap(organ =>
    organ.conditions
      .filter(condition => condition.marked_in_survey)
      .map(condition => ({ organ, condition })),
  );
}

/** The intro slide's carousel id, which its own events report as `slide_id`. */
export const CONDITIONS_INTRO_SLIDE_ID = 'conditions-intro';

/** A spotlight's carousel id, which its own events report as `slide_id`. */
export const conditionSpotlightSlideId = (
  condition: Pick<GutBodyConnectionsCondition, 'condition'>,
) => `condition-${condition.condition}`;

/** "N of M metrics flagged", or the empty state when nothing is flagged. */
export function conditionFlagsLabel(
  condition: GutBodyConnectionsCondition,
): string {
  return condition.flagged_count > 0
    ? `${condition.flagged_count} of ${condition.total_count} metrics flagged`
    : 'No associations flagged';
}

export type IntroBodyVariant =
  | 'adultMale'
  | 'adultFemale'
  | 'adultPregnant'
  | 'child'
  | 'babyToddler';

interface IntroBodyQuery {
  /** `kit.age_at_sampling`, in days. */
  age: number;
  sex: string;
  pregnant: boolean;
}

/**
 * Which body the intro draws, so we can pick its matching tag overlay. Mirrors
 * how `GutBodyConnectionsBody` resolves a body from the same kit fields.
 */
export function resolveIntroBodyVariant({
  age,
  sex,
  pregnant,
}: IntroBodyQuery): IntroBodyVariant {
  const ageGroup = getAgeGroup(age);
  if (ageGroup === AgeGroup.BABY || ageGroup === AgeGroup.TODDLER) {
    return 'babyToddler';
  }
  if (ageGroup === AgeGroup.CHILD) {
    return 'child';
  }
  if (pregnant) {
    return 'adultPregnant';
  }
  return sex === 'F' ? 'adultFemale' : 'adultMale';
}

/**
 * Pre-baked tag overlays (dashed leaders + labelled pills) laid over the intro
 * body, one per rendered body. A variant with no entry keeps `GutBodyConnectionsBody`'s
 * own labels until its overlay is added here.
 */
export const INTRO_TAGS_OVERLAYS: Partial<Record<IntroBodyVariant, string>> = {
  adultMale: conditionsIntroTagsAdultMale,
  adultFemale: conditionsIntroTagsAdultFemale,
  adultPregnant: conditionsIntroTagsAdultPregnant,
  child: conditionsIntroTagsChild,
  babyToddler: conditionsIntroTagsBabyToddler,
};
